import { Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { EnumScreenList, EnumTimedProjectStatus } from 'tdc-web-backend/enums/enums';
import { useTranslation } from 'react-i18next';
import TimeProjectsListCards from './TimedProjectsList/TimeProjectsListCards';
import CustomTable from '../../../components/custom-table/CustomTable';
import StatusChipField from '../../../components/StatusChipField/StatusChipField';
import { formatDateFns } from '../../../utils/helpers';
import BannerNotificationMapper from '../../../components/notifications/bannerNotifications/BannerNotificationMapper';
import useAuth from '../../../utils/hooks/useAuth';

const TimedProjects = () => {
  const navigate = useNavigate();
  const {
    authData: { userData: currentUser },
  } = useAuth();
  const { t, i18n } = useTranslation();

  const columns: GridColDef[] = [
    {
      field: 'status',
      headerName: t('common.status'),
      flex: 0.2,
      renderCell: (params: GridRenderCellParams) => <StatusChipField text={params.value} />,
    },
    {
      field: 'name',
      headerName: t('common.name'),
      flex: 0.3,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" fontWeight={600} sx={{ color: 'primaryDark.600' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'published',
      headerName: t('common.published'),
      flex: 0.15,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">{formatDateFns(params.value, i18n.language, '/')}</Typography>
      ),
    },
    {
      field: 'closed',
      headerName: t('common.closed'),
      flex: 0.15,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">{formatDateFns(params.value, i18n.language, '/')}</Typography>
      ),
    },
    {
      field: 'pitchCount',
      headerName: t('common.pitches'),
      flex: 0.15,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">{params.value}</Typography>
      ),
    },
  ];

  const panelConfig = [
    {
      id: 'timed-projects_all',
      title: t('filters.all'),
    },
    {
      id: 'timed-projects_active',
      title: t('filters.active'),
      search: { status: EnumTimedProjectStatus.Active },
    },
    {
      id: 'timed-projects_closed',
      title: t('filters.closed'),
      search: { status: EnumTimedProjectStatus.Closed },
    },
    {
      id: 'timed-projects_pending',
      title: t('filters.pending'),
      search: { status: EnumTimedProjectStatus.Pending },
    },
    {
      id: 'timed-projects_draft',
      title: t('filters.draft'),
      search: { status: EnumTimedProjectStatus.Draft },
    },
  ];

  return (
    <Stack direction="column" spacing={4} height="100%">
      <BannerNotificationMapper screen={EnumScreenList.TimedProjectScreen} />
      <TimeProjectsListCards />
      <CustomTable
        resource="timed-projects"
        searchParams={{
          company: currentUser?.membership?.company,
        }}
        columns={columns}
        panelConfig={panelConfig}
        onRowClick={(params) => navigate(`./${params.id}`)}
        autoHeight
        hideFooter
      />
    </Stack>
  );
};

export default TimedProjects;
